import { useState, useEffect, useRef, useCallback, memo } from 'react';
import FloatingLines from './FloatingLines';
import './App.css';

/* ── Animated Counter ── */
function AnimatedStat({ end, suffix = '', label }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 2000;
          const startTime = performance.now();
          const numEnd = parseFloat(end);

          const tick = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * numEnd));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return (
    <div className="stat-item" ref={ref}>
      <div className="stat-number">{count}{suffix}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

/* ── Fade-in on scroll ── */
const FadeIn = memo(function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`fade-in ${visible ? 'visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
});

/* ── Hero Device Showcase ── */
const showcaseSlides = ['imperial', 'newportexotics', 'spdy', 'thejoyfulgifts'];

function HeroShowcase({ paused }) {
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState([0]);

  useEffect(() => {
    if (paused) return;
    let timer;
    const start = () => {
      timer = setInterval(() => {
        if (document.hidden) return;
        setActive(prev => {
          const next = (prev + 1) % showcaseSlides.length;
          setLoaded(l => l.includes(next) ? l : [...l, next]);
          return next;
        });
      }, 3500);
    };
    start();
    const handleVisibility = () => {
      if (document.hidden) { clearInterval(timer); } else { start(); }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [paused]);

  return (
    <div className={`hero-showcase${paused ? ' showcase-paused' : ''}`}>
      <div className="device-laptop">
        <div className="laptop-frame">
          <div className="laptop-screen">
            {showcaseSlides.map((name, i) => (
              loaded.includes(i) ? (
                <img
                  key={name}
                  src={`/${name}.png`}
                  alt={name}
                  className={`device-screenshot${i === active ? ' active' : ''}`}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              ) : null
            ))}
          </div>
        </div>
      </div>
      <div className="device-phone">
        <div className="phone-frame">
          <div className="phone-screen">
            {showcaseSlides.map((name, i) => (
              loaded.includes(i) ? (
                <img
                  key={name}
                  src={`/${name}_mobile.png`}
                  alt={name}
                  className={`device-screenshot${i === active ? ' active' : ''}`}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              ) : null
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Navbar (isolated to prevent re-renders on scroll) ── */
function Navbar({ onGetStarted, onShowSupport }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const handleScroll = () => {
      if (window.scrollY > 20) {
        nav.classList.add('navbar-scrolled');
      } else {
        nav.classList.remove('navbar-scrolled');
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => setMobileMenuOpen(false);

  return (
    <nav className="navbar" ref={navRef}>
      <div className="navbar-inner">
        <a href="#top" className="navbar-logo">Troye<span className="logo-dot">.</span></a>
        <ul className={`navbar-links ${mobileMenuOpen ? 'open' : ''}`}>
          <li><a href="#work" onClick={handleNavClick}>Work</a></li>
          <li><a href="#about" onClick={handleNavClick}>About</a></li>
          <li><a href="#process" onClick={handleNavClick}>Process</a></li>
          <li><a href="#pricing" onClick={handleNavClick}>Pricing</a></li>
          <li><a href="#support" onClick={e => { e.preventDefault(); handleNavClick(); onShowSupport(); }}>Support</a></li>
          <li>
            <button className="btn-get-started" onClick={() => { handleNavClick(); onGetStarted(); }}>
              Get Started
            </button>
          </li>
        </ul>
        <button
          className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>
      </div>
    </nav>
  );
}

const FORMSPREE_URL = 'https://formspree.io/f/mqegqedg';

const PROJECTS = [
  { name: 'Imperial Tailors', category: 'Fashion & Tailoring', description: 'Premium custom tailoring — refined brand presence built for trust and elegance.', tags: ['Web Design', 'Responsive', 'Branding'], image: '/imperial.png', frost: 'rgba(50, 30, 8, 0.58)', url: 'https://imperialtailors.ca' },
  { name: 'The Joyful Gifts', category: 'E-Commerce', description: 'A warm, inviting gift shop experience designed to maximize browsing and sales.', tags: ['E-Commerce', 'UI/UX', 'SEO'], image: '/thejoyfulgifts.png', frost: 'rgba(160, 55, 30, 0.55)', url: 'https://thejoyfulgifts.com' },
  { name: 'Newport Exotics', category: 'Automotive & Luxury', description: 'Sleek, high-end automotive showcase built to match the luxury of the brand.', tags: ['Luxury Design', 'Gallery', 'Performance'], image: '/newportexotics.png', frost: 'rgba(8, 8, 20, 0.65)', url: 'https://newport-exotics.com' },
  { name: 'SPDY Junk Removal', category: 'Local Services', description: 'High-converting service page that drives calls and booking for local junk removal.', tags: ['Lead Gen', 'Responsive', 'Local SEO'], image: '/spdy.png', frost: 'rgba(15, 40, 80, 0.58)', url: 'https://spdyjunkremoval.com' },
];

function WorkGrid() {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? PROJECTS : PROJECTS.slice(0, 2);
  return (
    <>
      <div className="work-grid">
        {visible.map((project, i) => (
          <FadeIn key={project.name} delay={i * 100}>
            <a href={project.url} target="_blank" rel="noopener noreferrer" className="work-card">
              <div className="work-card-image">
                <div className="browser-chrome">
                  <div className="browser-dots">
                    <span className="dot dot-red" />
                    <span className="dot dot-yellow" />
                    <span className="dot dot-green" />
                  </div>
                  <div className="browser-bar">{project.url.replace('https://', '')}</div>
                </div>
                <div className="browser-screen">
                  <img src={project.image} alt={project.name} className="work-card-img" />
                  <div className="work-card-overlay">
                    <span className="work-card-category">{project.category}</span>
                  </div>
                </div>
              </div>
              <div className="work-card-info" style={{ background: project.frost }}>
                <h3>{project.name}</h3>
                <p>{project.description}</p>
                <div className="work-card-tags">
                  {project.tags.map(tag => <span key={tag} className="work-tag">{tag}</span>)}
                </div>
              </div>
            </a>
          </FadeIn>
        ))}
      </div>
      <div className="work-show-more">
        <button className="btn-outline" onClick={() => setShowAll(v => !v)}>
          {showAll ? <>Show Less <span className="btn-arrow">−</span></> : <>Show More Work <span className="btn-arrow">+</span></>}
        </button>
      </div>
    </>
  );
}

function FounderCards() {
  const [expanded, setExpanded] = useState(new Set());
  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const founders = [
    { id: 1, name: 'Tyson Wahab', role: 'Co-Founder & Developer', location: 'Ontario, Canada', email: 'tyson@troye.ca', bio: 'Full-stack developer with an eye for clean design and seamless user experiences that drive real results.' },
    { id: 2, name: 'Roy Chang', role: 'Co-Founder & Developer', location: 'Los Angeles, USA', email: 'roy@troye.us', bio: 'Creative strategist and frontend specialist who turns brand vision into pixel-perfect digital realities.' },
  ];
  return (
    <div className="about-founders-stack">
      {founders.map((f, i) => (
        <FadeIn key={f.id} delay={i * 100}>
          <div
            className={`founder-card founder-card-${f.id}${expanded.has(f.id) ? ' expanded' : ''}`}
            onClick={() => toggle(f.id)}
          >
            <div className="founder-card-top">
              <span className="material-symbols-rounded founder-card-avatar">person</span>
              <div className="founder-card-location">{f.location}</div>
            </div>
            <div className="founder-card-name">{f.name}</div>
            <div className="founder-card-role">{f.role}</div>

            <span className={`founder-card-chevron material-symbols-rounded${expanded.has(f.id) ? ' open' : ''}`}>expand_more</span>
            <p className="founder-card-bio">{f.bio}</p>
            <a className="founder-card-email" href={`mailto:${f.email}`} onClick={e => e.stopPropagation()}>
              <span className="material-symbols-rounded" style={{ fontSize: '14px', verticalAlign: 'middle', marginRight: '5px' }}>mail</span>
              {f.email}
            </a>
          </div>
        </FadeIn>
      ))}
    </div>
  );
}

function App() {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('contact');
  const [formStatus, setFormStatus] = useState('idle'); // idle | sending | success | error
  const [showSupport, setShowSupport] = useState(false);

  const handleShowSupport = useCallback(() => {
    setShowSupport(true);
    setTimeout(() => {
      document.getElementById('support')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, []);

  const openModal = useCallback((type = 'contact') => {
    setModalType(type);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setFormStatus('idle');
    document.body.style.overflow = '';
  }, []);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    const data = new FormData(e.target);
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' }
      });
      if (res.ok) {
        setFormStatus('success');
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  }, []);

  return (
    <div className="app">
      {/* ── Navbar ── */}
      <Navbar onGetStarted={() => openModal('contact')} onShowSupport={handleShowSupport} />

      {/* ── Hero ── */}
      <section className="hero" id="top">
        <div className="hero-bg">
          {!modalOpen && (
            <FloatingLines
              enabledWaves={["middle", "top"]}
              backgroundColor="#EBF4F8"
              linesGradient={["#1a3a5c", "#2c5f8d", "#6B8DAE"]}
              lineCount={3}
              lineDistance={35}
              interactive={false}
              parallax={false}
              animationSpeed={0.6}
              mixBlendMode="normal"
            />
          )}
        </div>

        <div className="hero-content">
          <div className="hero-inner">

            <FadeIn>
              <div className="hero-label">
                <span className="hero-label-line" />
                <span className="hero-label-text">Premium Web Design</span>
              </div>
            </FadeIn>

            <FadeIn delay={100}>
              <h1 className="hero-title">
                <span className="hero-title-troye">Troye<span className="logo-dot">.</span></span>{' '}
                <span className="hero-title-digital">Digital</span>
              </h1>
            </FadeIn>

            <FadeIn delay={200}>
              <p className="hero-subtitle">Built For Growth</p>
            </FadeIn>

            <FadeIn delay={300}>
              <p className="hero-description">
                We build fast, beautiful, and high-converting websites for businesses
                that want to stand out — not blend in.
              </p>
            </FadeIn>

            <FadeIn delay={400}>
              <div className="hero-buttons">
                <a href="#work" className="btn-primary">
                  View Our Work <span className="btn-arrow">→</span>
                </a>
                <a href="#pricing" className="btn-secondary">
                  See Pricing
                </a>
              </div>
            </FadeIn>

            <FadeIn delay={500}>
              <div className="hero-stats">
                <AnimatedStat end={25} suffix="+" label="Projects Delivered" />
                <AnimatedStat end={100} suffix="%" label="Client Satisfaction" />
                <div className="stat-item">
                  <div className="stat-number">3 days</div>
                  <div className="stat-label">Avg. Turnaround</div>
                </div>
              </div>
            </FadeIn>
          </div>
          <HeroShowcase paused={modalOpen} />
        </div>
      </section>

      {/* ── About ── */}
      <section className="section about" id="about">
        <div className="section-container">
          <div className="about-grid">

            {/* Left: stacked founder cards — hidden on mobile, shown via about-right on mobile */}
            <div className="about-founders-col desktop-only">
              <FounderCards />
            </div>

            {/* Right: heading + values */}
            <div className="about-right">
              <FadeIn>
                <div className="section-label">
                  <span className="hero-label-line" />
                  <span className="hero-label-text">Who We Are</span>
                </div>
              </FadeIn>
              <FadeIn delay={100}>
                <h2 className="section-title about-title">Two builders,<br />one mission.</h2>
              </FadeIn>
              <div className="mobile-only" style={{ marginBottom: '24px' }}>
                <FounderCards />
              </div>
              <FadeIn delay={200}>
                <p className="about-text">
                  We're not an agency with layers of overhead. We're two guys who genuinely love building websites — and we're really good at it.
                </p>
              </FadeIn>
              <div className="about-values">
                {[
                  {
                    icon: 'chat',
                    title: 'Direct Communication',
                    desc: 'You talk to us — the people actually building your site. No middlemen, no miscommunication.'
                  },
                  {
                    icon: 'bolt',
                    title: 'Speed That Matters',
                    desc: 'Most projects launch within a week. We move fast without cutting corners.'
                  },
                  {
                    icon: 'verified',
                    title: 'Built to Perform',
                    desc: 'Fast load times, mobile-first, and SEO-ready out of the box. Every single time.'
                  }
                ].map((value, i) => (
                  <FadeIn key={value.title} delay={300 + i * 100}>
                    <div className="value-card">
                      <span className="material-symbols-rounded value-card-icon">{value.icon}</span>
                      <div className="value-card-text">
                        <h4>{value.title}</h4>
                        <p>{value.desc}</p>
                      </div>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Work ── */}
      <section className="section work" id="work">
        <div className="section-container">
          <div className="section-header">
            <FadeIn>
              <div className="section-label">
                <span className="hero-label-line" />
                <span className="hero-label-text">Selected Work</span>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <h2 className="section-title">Websites we've brought to life.</h2>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="section-description">
                Real projects, real clients, real results. Click through to see them live.
              </p>
            </FadeIn>
          </div>

          <WorkGrid />
        </div>
      </section>

      {/* ── Process ── */}
      <section className="section process" id="process">
        <div className="section-container">
          <div className="section-header">
            <FadeIn>
              <div className="section-label">
                <span className="hero-label-line" />
                <span className="hero-label-text">How It Works</span>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <h2 className="section-title">From idea to launch in four steps.</h2>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="section-description">
                A streamlined process that keeps things simple, fast, and transparent from start to finish.
              </p>
            </FadeIn>
          </div>

          <div className="process-grid">
            {[
              {
                icon: 'chat',
                title: 'Discovery',
                desc: "We hop on a quick call to understand your business, goals, and vibe. No fluff."
              },
              {
                icon: 'palette',
                title: 'Design',
                desc: "We craft a custom mockup and iterate until you're completely in love with it."
              },
              {
                icon: 'code',
                title: 'Develop',
                desc: 'We build it — fast, clean, responsive, and optimized for search engines.'
              },
              {
                icon: 'rocket_launch',
                title: 'Launch',
                desc: 'We deploy your site, run QA checks, and hand you the keys. You\'re live.'
              }
            ].map((step, i) => (
              <FadeIn key={step.title} delay={i * 150} className="process-step-wrapper">
                {i > 0 && <div className="process-connector"><span className="material-symbols-rounded">arrow_forward</span></div>}
                <div className="process-step">
                  <div className="process-icon">
                    <span className="material-symbols-rounded">{step.icon}</span>
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="section pricing" id="pricing">
        <div className="section-container">
          <div className="section-header">
            <FadeIn>
              <div className="section-label">
                <span className="hero-label-line" />
                <span className="hero-label-text">Pricing</span>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <h2 className="section-title">Simple, Transparent Pricing</h2>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="section-description">
                No hidden fees, no bloated packages. You get a professional website at a fair price — period.
              </p>
            </FadeIn>
          </div>

          <div className="pricing-grid">
            <FadeIn delay={0}>
              <div className="pricing-card">
                <div className="pricing-tier">Starter Site</div>
                <div className="pricing-price">$499</div>
                <div className="pricing-currency">USD <span className="pricing-cad">≈ $689 CAD</span></div>
                <ul className="pricing-features">
                  <li>Custom responsive design</li>
                  <li>Up to 5 pages</li>
                  <li>Mobile-first approach</li>
                  <li>Contact form integration</li>
                  <li>Basic SEO setup</li>
                  <li>7-day delivery</li>
                </ul>
                <button className="btn-secondary pricing-btn" onClick={() => openModal('starter')}>
                  Get Started
                </button>
              </div>
            </FadeIn>

            <FadeIn delay={150}>
              <div className="pricing-card pricing-featured">
                <div className="pricing-badge">Most Popular</div>
                <div className="pricing-tier">Business Pro</div>
                <div className="pricing-price-row">
                  <div className="pricing-price">$649</div>
                  <div className="pricing-original">$1,299</div>
                </div>
                <div className="pricing-currency">USD <span className="pricing-cad">≈ $896 CAD</span></div>
                <div className="pricing-save">Save $650 — Launch Special</div>
                <ul className="pricing-features">
                  <li>Everything in Starter</li>
                  <li>Unlimited pages</li>
                  <li>Custom animations & interactions</li>
                  <li>Advanced SEO optimization</li>
                  <li>Analytics integration</li>
                  <li>3-day delivery</li>
                </ul>
                <button className="btn-primary pricing-btn" onClick={() => openModal('business')}>
                  Get Started
                </button>
              </div>
            </FadeIn>

            <FadeIn delay={300}>
              <div className="pricing-card">
                <div className="pricing-tier">Custom Web</div>
                <div className="pricing-price">Custom</div>
                <div className="pricing-currency">Quote based on your needs</div>
                <ul className="pricing-features">
                  <li>Everything in Business Pro</li>
                  <li>E-commerce integration</li>
                  <li>Custom functionality & features</li>
                  <li>Third-party API integrations</li>
                  <li>Database & CMS setup</li>
                  <li>Unlimited revisions</li>
                  <li>Priority support & maintenance</li>
                </ul>
                <button className="btn-secondary pricing-btn" onClick={() => openModal('custom')}>
                  Contact Us
                </button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── Support ── */}
      <section className="section support" id="support" style={{ display: showSupport ? 'block' : 'none' }}>
        <div className="section-container">
          <div className="section-header">
            <FadeIn>
              <div className="section-label">
                <span className="hero-label-line" />
                <span className="hero-label-text">Support</span>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <h2 className="section-title">Already have a website with us?</h2>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="section-description">
                We're here to help keep it running smoothly.
              </p>
            </FadeIn>
          </div>

          <div className="support-cards desktop-only">
            <FadeIn delay={100}>
              <div className="support-card">
                <span className="material-symbols-rounded support-card-icon">handyman</span>
                <h3>Site Maintenance</h3>
                <ul>
                  <li>Content updates</li>
                  <li>Bug fixes</li>
                  <li>Performance tuning</li>
                  <li>Security patches</li>
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="support-card">
                <span className="material-symbols-rounded support-card-icon">add_circle</span>
                <h3>New Features</h3>
                <ul>
                  <li>New pages or sections</li>
                  <li>Forms & integrations</li>
                  <li>E-commerce additions</li>
                  <li>Custom functionality</li>
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={300}>
              <div className="support-card">
                <span className="material-symbols-rounded support-card-icon">headset_mic</span>
                <h3>General Support</h3>
                <ul>
                  <li>Technical questions</li>
                  <li>Domain & hosting help</li>
                  <li>Analytics setup</li>
                  <li>SEO guidance</li>
                </ul>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={300}>
            <div className="support-card mobile-only" style={{ marginBottom: '32px' }}>
              <span className="material-symbols-rounded support-card-icon">headset_mic</span>
              <h3>General Support</h3>
              <ul>
                <li>Technical questions</li>
                <li>Domain & hosting help</li>
                <li>Analytics setup</li>
                <li>SEO guidance</li>
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={400}>
            <div className="support-cta">
              <p className="support-cta-text">Need help with your website or have a question? We'd love to hear from you.</p>
              <button className="btn-primary support-btn" onClick={() => openModal('support')}>
                Get Support <span className="btn-arrow">→</span>
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section cta">
        <div className="cta-bg">
          <FloatingLines
            enabledWaves={["middle"]}
            backgroundColor="#181A2A"
            linesGradient={["#2c5f8d", "#6B8DAE", "#D4A24A"]}
            lineCount={3}
            lineDistance={40}
            bendRadius={5}
            bendStrength={-1}
            interactive={true}
            parallax={true}
            animationSpeed={0.4}
            mixBlendMode="normal"
          />
        </div>
        <div className="section-container cta-content">
          <FadeIn>
            <h2 className="cta-title">Ready to level up your online presence?</h2>
          </FadeIn>
          <FadeIn delay={100}>
            <p className="cta-text">
              Let's chat about your project. No commitment, no pressure — just two guys who want to help your business grow.
            </p>
          </FadeIn>
          <FadeIn delay={200}>
            <button className="btn-primary btn-cta" onClick={() => openModal('contact')}>
              Start Your Project <span className="btn-arrow">→</span>
            </button>
          </FadeIn>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <div className="section-container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="navbar-logo footer-logo">Troye<span className="logo-dot">.</span></div>
              <p className="footer-tagline">Premium web design, built for growth.</p>
            </div>
            <div className="footer-col">
              <h4>Navigate</h4>
              <a href="#work">Work</a>
              <a href="#about">About</a>
              <a href="#pricing">Pricing</a>
              <a href="#process">Process</a>
              <a href="#support">Support</a>
            </div>
            <div className="footer-col">
              <h4>Connect</h4>
              <button className="footer-link-btn" onClick={() => openModal('contact')}>Contact Us</button>
              <a href="https://www.instagram.com/troye_digital/" target="_blank" rel="noopener noreferrer">Instagram</a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-bottom-left">
              <span>&copy; 2026 Troye Digital. All rights reserved.</span>
              <button className="footer-link-btn footer-privacy-btn" onClick={() => openModal('privacy')}>Privacy Policy</button>
            </div>
            <p className="footer-credit">Designed & Built by Tyson & Roy</p>
          </div>
        </div>
      </footer>

      {/* ── Modal ── */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>

            {modalType === 'privacy' ? (
              <div className="privacy-modal-content">
                <h3 className="modal-title">Privacy Policy</h3>
                <p className="modal-subtitle">Last updated: March 2026</p>
                <div className="privacy-body">
                  <h4>1. Who We Are</h4>
                  <p>Troye Digital ("we", "us", "our") is a web design agency operated by Tyson Wahab (Ontario, Canada) and Roy Chang (Los Angeles, USA). We can be reached at <a href="mailto:tyson@troye.ca">tyson@troye.ca</a> or <a href="mailto:roy@troye.us">roy@troye.us</a>.</p>

                  <h4>2. Information We Collect</h4>
                  <p>When you submit a form on our website, we collect the information you provide — such as your name, email address, phone number, and project details. We do not collect any information passively beyond standard web analytics (see Section 5).</p>

                  <h4>3. How We Use Your Information</h4>
                  <p>We use the information you provide solely to respond to your inquiry and discuss potential or ongoing work. We do not sell, rent, or share your personal information with third parties for marketing purposes.</p>

                  <h4>4. Legal Basis &amp; Applicable Law</h4>
                  <p><strong>Canada (PIPEDA):</strong> We collect personal information only with your knowledge and consent, for purposes that are reasonable and disclosed at the time of collection, in accordance with the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA).</p>
                  <p><strong>United States (CCPA &amp; State Laws):</strong> We do not sell personal information. California residents have the right to request disclosure of the categories of personal information collected, request deletion of their data, and opt out of any sale (we do not sell data). To exercise these rights, contact us at the emails above.</p>

                  <h4>5. Analytics</h4>
                  <p>We use Google Analytics to understand aggregate traffic patterns on our website. This service may set cookies and collect anonymized data about your visit. You can opt out via the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.</p>

                  <h4>6. Form Submissions</h4>
                  <p>Contact forms on this site are processed by <a href="https://formspree.io" target="_blank" rel="noopener noreferrer">Formspree</a>. Submissions are transmitted securely and stored on Formspree's servers. Please review <a href="https://formspree.io/legal/privacy-policy" target="_blank" rel="noopener noreferrer">Formspree's Privacy Policy</a> for details on how they handle your data.</p>

                  <h4>7. Data Retention</h4>
                  <p>We retain contact form submissions only as long as necessary to respond to your inquiry or fulfill any ongoing engagement. You may request deletion of your data at any time by emailing us.</p>

                  <h4>8. Your Rights</h4>
                  <p>Regardless of your location, you have the right to access, correct, or request deletion of any personal information we hold about you. To exercise any of these rights, contact us at <a href="mailto:tyson@troye.ca">tyson@troye.ca</a>.</p>

                  <h4>9. Cookies</h4>
                  <p>This site uses cookies only for analytics (Google Analytics). No tracking or advertising cookies are used. You may disable cookies in your browser settings at any time.</p>

                  <h4>10. Changes to This Policy</h4>
                  <p>We may update this policy from time to time. The "Last updated" date at the top of this page reflects the most recent revision. Continued use of our site after changes constitutes acceptance of the updated policy.</p>
                </div>
              </div>
            ) : modalType === 'support' ? (
              <>
                <h3 className="modal-title">How can we help?</h3>
                <p className="modal-subtitle">
                  Whether you're an existing client or just have a question — we're here for you.
                </p>
                <form className="contact-form" onSubmit={handleFormSubmit}>
                  <input type="hidden" name="form-type" value="Support Request" />
                  <div className="form-row">
                    <div className="form-group">
                      <label>Your Name</label>
                      <input type="text" name="name" placeholder="John Doe" required />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input type="email" name="email" placeholder="john@company.com" required />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone <span className="form-optional">(optional)</span></label>
                      <input type="tel" name="phone" placeholder="+1 (555) 000-0000" />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <select name="country" defaultValue="">
                        <option value="" disabled>Select country</option>
                        <option>United States</option>
                        <option>Canada</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>I am a...</label>
                      <select name="client-type" defaultValue="">
                        <option value="" disabled>Select one</option>
                        <option>Existing Client</option>
                        <option>Potential Client</option>
                        <option>General Inquiry</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Issue Type</label>
                      <select name="issue-type" defaultValue="">
                        <option value="" disabled>Select a category</option>
                        <option>Site Update / Content Change</option>
                        <option>Bug or Broken Feature</option>
                        <option>New Feature Request</option>
                        <option>Performance Issue</option>
                        <option>Billing / Account</option>
                        <option>General Question</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Website URL <span className="form-optional">(if applicable)</span></label>
                    <input type="url" name="website-url" placeholder="https://yoursite.com" />
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <div className="priority-options">
                      {['Low', 'Medium', 'High', 'Urgent'].map(level => (
                        <label key={level} className={`priority-chip priority-${level.toLowerCase()}`}>
                          <input type="radio" name="priority" value={level} />
                          <span>{level}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Describe Your Issue</label>
                    <textarea name="message" rows={4} placeholder="Tell us what's going on — the more detail, the faster we can help." />
                  </div>
                  {formStatus === 'success' && <p className="form-success">Message sent! We'll be in touch soon.</p>}
                  {formStatus === 'error' && <p className="form-error">Something went wrong. Please try again.</p>}
                  <button type="submit" className="btn-primary form-submit" disabled={formStatus === 'sending'}>
                    {formStatus === 'sending' ? 'Sending...' : <>Submit Request <span className="btn-arrow">→</span></>}
                  </button>
                </form>
              </>
            ) : (
              <>
                <h3 className="modal-title">Start Your Project</h3>
                <p className="modal-subtitle">
                  Drop your details and we'll get back to you within 24 hours.
                </p>
                <form className="contact-form" onSubmit={handleFormSubmit}>
                  <input type="hidden" name="form-type" value="Project Inquiry" />
                  <div className="form-row">
                    <div className="form-group">
                      <label>Your Name</label>
                      <input type="text" name="name" placeholder="John Doe" required />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input type="email" name="email" placeholder="john@company.com" required />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone <span className="form-optional">(optional)</span></label>
                      <input type="tel" name="phone" placeholder="+1 (555) 000-0000" />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <select name="country" defaultValue="">
                        <option value="" disabled>Select country</option>
                        <option>United States</option>
                        <option>Canada</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Interested In</label>
                      <select name="plan" defaultValue={
                        modalType === 'starter' ? 'Starter Site' :
                        modalType === 'business' ? 'Business Pro' :
                        modalType === 'custom' ? 'Custom Web' : ''
                      }>
                        <option value="" disabled>Select a plan</option>
                        <option>Starter Site</option>
                        <option>Business Pro</option>
                        <option>Custom Web</option>
                        <option>Not Sure Yet</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Budget Range</label>
                      <select name="budget" defaultValue="">
                        <option value="" disabled>Select a range</option>
                        <option>Under $500</option>
                        <option>$500 – $1,000</option>
                        <option>$1,000 – $2,500</option>
                        <option>$2,500 – $5,000</option>
                        <option>$5,000+</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Project Type</label>
                    <select name="project-type" defaultValue="">
                      <option value="" disabled>Select a type</option>
                      <option>Business Website</option>
                      <option>E-Commerce</option>
                      <option>Portfolio / Personal</option>
                      <option>Landing Page</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tell Us More</label>
                    <textarea name="message" rows={4} placeholder="What are you looking to build?" />
                  </div>
                  {formStatus === 'success' && <p className="form-success">Message sent! We'll be in touch soon.</p>}
                  {formStatus === 'error' && <p className="form-error">Something went wrong. Please try again.</p>}
                  <button type="submit" className="btn-primary form-submit" disabled={formStatus === 'sending'}>
                    {formStatus === 'sending' ? 'Sending...' : <>Send Message <span className="btn-arrow">→</span></>}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
